import React, { useState } from "react";
import { toast } from "sonner";
import { Field, Input, Textarea, Select, Checkbox, RadioPills, SubmitButton } from "./FormPrimitives";
import { FormSuccess } from "./FormSuccess";
import { submitIntake } from "../../lib/api";
import { IMMEDIATE_NEEDS } from "../../data/content";

const initial = {
  full_name: "", phone: "", email: "", veteran_status: "",
  housing_situation: "", homeless_or_at_risk: "", city_state: "",
  immediate_needs: [], preferred_contact: "", notes: "", consent: false,
};

export const IntakeForm = ({ prefill }) => {
  const [form, setForm] = useState({ ...initial, ...(prefill || {}) });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };
  const toggleNeed = (need) => set("immediate_needs", form.immediate_needs.includes(need) ? form.immediate_needs.filter((n) => n !== need) : [...form.immediate_needs, need]);

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your full name.";
    if (!form.phone.trim()) e.phone = "A phone number helps us reach you.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.veteran_status) e.veteran_status = "Please select your veteran status.";
    if (!form.housing_situation.trim()) e.housing_situation = "Tell us your current situation.";
    if (!form.homeless_or_at_risk) e.homeless_or_at_risk = "Please choose one.";
    if (!form.city_state.trim()) e.city_state = "City and state help us respond locally.";
    if (!form.preferred_contact) e.preferred_contact = "How should we reach you?";
    if (!form.consent) e.consent = "We need your consent to follow up.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Please review the highlighted fields."); return; }
    setLoading(true);
    try {
      const res = await submitIntake(form);
      setDone(res);
      toast.success("Request received.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <FormSuccess
        testid="intake-success"
        title="You've taken the first step."
        message={done.message}
        refId={done.ref_id}
        onReset={() => { setForm(initial); setDone(null); }}
      />
    );
  }

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 gap-6" data-testid="intake-form" noValidate>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Full name" required htmlFor="full_name" error={errors.full_name}>
          <Input id="full_name" data-testid="intake-full-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="First and last name" />
        </Field>
        <Field label="Phone" required htmlFor="phone" error={errors.phone}>
          <Input id="phone" data-testid="intake-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(000) 000-0000" />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Email" required htmlFor="email" error={errors.email}>
          <Input id="email" type="email" data-testid="intake-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
        </Field>
        <Field label="City / State" required htmlFor="city_state" error={errors.city_state}>
          <Input id="city_state" data-testid="intake-city-state" value={form.city_state} onChange={(e) => set("city_state", e.target.value)} placeholder="e.g. Raleigh, NC" />
        </Field>
      </div>

      <Field label="Veteran status" required htmlFor="veteran_status" error={errors.veteran_status}>
        <Select id="veteran_status" data-testid="intake-veteran-status" value={form.veteran_status} onChange={(e) => set("veteran_status", e.target.value)}>
          <option value="">Select…</option>
          <option>Veteran</option>
          <option>Active duty transitioning</option>
          <option>National Guard / Reserve</option>
          <option>Not sure / other</option>
        </Select>
      </Field>

      <Field label="Current housing situation" required htmlFor="housing_situation" error={errors.housing_situation}>
        <Textarea id="housing_situation" data-testid="intake-housing" value={form.housing_situation} onChange={(e) => set("housing_situation", e.target.value)} placeholder="Briefly describe where you're staying now." />
      </Field>

      <Field label="Are you homeless or at risk of homelessness?" required error={errors.homeless_or_at_risk}>
        <RadioPills name="intake-risk" options={["Yes", "At risk", "No"]} value={form.homeless_or_at_risk} onChange={(v) => set("homeless_or_at_risk", v)} />
      </Field>

      <Field label="Immediate needs" hint="Select all that apply.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {IMMEDIATE_NEEDS.map((need) => (
            <Checkbox key={need} id={`need-${need.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} label={need} checked={form.immediate_needs.includes(need)} onChange={() => toggleNeed(need)} />
          ))}
        </div>
      </Field>

      <Field label="Preferred contact method" required error={errors.preferred_contact}>
        <RadioPills name="intake-contact" options={["Phone", "Text", "Email"]} value={form.preferred_contact} onChange={(v) => set("preferred_contact", v)} />
      </Field>

      <Field label="Anything else we should know?" htmlFor="notes">
        <Textarea id="notes" data-testid="intake-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Optional — share whatever is helpful." />
      </Field>

      <Checkbox id="intake-consent" label="I consent to Iron Shield contacting me about my request. My information will be kept confidential and used only to coordinate support." checked={form.consent} onChange={(e) => set("consent", e.target.checked)} error={errors.consent} />

      <div><SubmitButton loading={loading} testid="intake-submit">Get Help</SubmitButton></div>
    </form>
  );
};

export default IntakeForm;
